/**
 * chat widget's Brain memory adapter — a thin wrapper over the shared
 * `@workspace/brain-client/chat` helpers (tdd-0042 Phase 1), pinning the widget's
 * app identity ("chat"). The recall/capture logic lives in the package now; this
 * file keeps the widget's existing call-site signatures stable and stamps the app.
 *
 * Best-effort throughout: failures degrade to "" / a silent no-op so chat never
 * blocks or breaks on memory. Matters for the embeddable widget, which runs on
 * hosts that may have no auth session (anon → Brain 401s → no memory, by design).
 */

import {
  captureExchange,
  captureFact,
  deleteMemory,
  detectRememberCue,
  recallFacts,
  factsToContext,
  hashTurn,
  BRAIN_MEMORY_HEADER,
  type Sensitivity,
} from "@workspace/brain-client/chat";

export { BRAIN_MEMORY_HEADER, hashTurn, factsToContext };

const APP = "chat";

export interface SavedMemory {
  /** The fact text stored to Brain. */
  fact: string;
  /** The new entry's id (for the undo/delete affordance), if surfaced. */
  id: string | null;
}

/**
 * If the user message is a "remember this" cue, extract the fact, write it as a
 * semantic memory, and return { fact, id } (id for the undo/delete bubble). Null
 * if the message isn't a remember-cue. Best-effort.
 */
export async function maybeCaptureRemembered(
  message: string,
  workspace = "personal",
): Promise<SavedMemory | null> {
  const fact = detectRememberCue(message);
  if (!fact) return null;
  const id = await captureFact(fact, { workspace, app: APP });
  return { fact, id };
}

/** Hard-delete a saved memory by id (undo a "remember…" save). */
export function removeMemory(id: string, workspace = "personal"): Promise<boolean> {
  return deleteMemory(id, { workspace, mode: "hard" });
}

/**
 * Search Brain for facts relevant to the user's message. Returns the individual
 * fact strings (for both the system-prompt injection and the recall UI bubble).
 */
export function fetchMemoryFacts(query: string, workspace = "personal"): Promise<string[]> {
  return recallFacts(query, { workspace });
}

/**
 * Auto-capture a completed exchange as an episodic memory (heuristic promotion).
 * `turnId` makes the write idempotent so re-sends/reloads don't duplicate.
 */
export function archiveExchange(
  prompt: string,
  response: string,
  turnId: string,
  workspace = "personal",
): Promise<void> {
  return captureExchange(prompt, response, { turnId, workspace, app: APP });
}

/** Explicit "remember this" — write a durable semantic fact (agent-sourced). Returns the entry id or null. */
export function archiveToMemory(
  content: string,
  opts: { sensitivity?: Sensitivity } = {},
  workspace = "personal",
): Promise<string | null> {
  return captureFact(content, { sensitivity: opts.sensitivity, workspace, app: APP });
}
